import React, { useState } from "react";
import PropTypes from "prop-types";
import { onSubmit } from "./BenchmarkUpload.functions";
import { useCbsCodes } from "../../api/cbs/CbsAPI";
import { ModalCompany } from "../project-create/CreateProject.components";
import CreateProjectSelectClientCompany from "../project-create/CreateProjectSelectClientCompany";

import "./BenchmarkUpload.css";
import "../dropzone/Dropzone.css";
import { useSensitivityLevelCodes } from "../../api/benchmarking/BenchmarkingAPI";
import { useCompanies } from "../../api/projects/ProjectCreateAPI";
import { ProjectLocation } from "./Benchmark.components";
import { CustomTooltip } from "../common/Tooltip";
import { InformationTooltipHtml } from "../post-contract-2.0/PostContractButtons.components";
import DragDropUploadFile from "../common/generic-upload-button/DragDropUploadFile";

export default function BenchmarkUpload(props) {
  const { setShow } = props;
  const { setBenchmarking } = props;

  const isNotPublished = false;

  const { cbsCodes } = useCbsCodes(isNotPublished);
  const { companies } = useCompanies(true);

  const [file, setFile] = useState([]);
  const [filePreview, setFilePreview] = useState([]);
  const [messages, setMessages] = useState([]);
  const [highlight, setHighlight] = useState(false);
  const [CBS, setCBS] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [measurementSystem, setMeasurementSystem] = useState("");
  const [infoSensLevel, setInfoSensLevel] = useState("");
  const [coordinates, setCoordinates] = useState({});

  const { sensitivityLevelCodes } = useSensitivityLevelCodes();

  const [companyId, setCompanyId] = useState("");
  const [company, setCompany] = useState({});

  const [error, setError] = useState({});

  // To reset File Input
  const [inputKey, setInputKey] = useState(0);

  let data = {
    file: file,
    CBS: CBS,
    sourceType: sourceType,
    measurementSystem: measurementSystem,
    setModal: setShow,
    setBenchmarking: setBenchmarking,
    setError: setError,
    setFile: setFile,
    setMessages: setMessages,
    setFilePreview: setFilePreview,
    informationSensitivityLevel: infoSensLevel,
    companyId: companyId === "" ? null : companyId,
    coordinates: coordinates,
  };

  const cbsOptions = [{ label: "Select CBS", value: "" }];
  cbsCodes?.data?.forEach((c) => {
    cbsOptions.push({ value: c, label: c });
  });

  const secLevelOptions = [{ label: "Select Sensitivity Level", value: "" }];
  sensitivityLevelCodes?.data?.forEach((c) => {
    secLevelOptions.push({ value: c.value, label: c.label });
  });

  const clientToBeMappedOptions = [{ label: "Select Client", value: "" }];
  if (companies.data.length > 0)
    companies?.data?.forEach((c) => {
      clientToBeMappedOptions.push({ value: c.id, label: c.name });
    });

  // Add a random query value to prevent caching
  const randomQueryValue = "?" + Date.now();

  return (
    <div className="benchmark-upload">
      <div className="benchmark-upload-control-container">
        <Upload
          inputKey={inputKey}
          setInputKey={setInputKey}
          setFile={setFile}
          setFilePreview={setFilePreview}
          setHighlight={setHighlight}
          highlight={highlight}
          setError={setError}
          setMessages={setMessages}
        />
        <div className="benchmark-upload-control">
          <div className="benchmark-upload-control-row">
            <div className="benchmark-upload-control-label">
              Benchmarking Guide:
            </div>
            <a
              className="benchmark-upload-document-button"
              href={
                "https://stcostclarityprd001.blob.core.windows.net/1-guides/Benchmarking Guide.pdf" +
                randomQueryValue
              }
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              Download
            </a>
          </div>
          <div className="benchmark-upload-control-row">
            <div className="benchmark-upload-control-label">
              Benchmarking Template:
            </div>
            <a
              className="benchmark-upload-document-button"
              href={
                "https://stcostclarityprd001.blob.core.windows.net/1-guides/Global CCM Data Capture Sheet.xlsm" +
                randomQueryValue
              }
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              Download
            </a>
          </div>
          <div className="benchmark-upload-control-row">
            <div className="benchmark-upload-control-label">
              Cost Breakdown Structure:
            </div>
            <select
              value={CBS}
              className="benchmark-upload-control-input"
              onChange={(e) => {
                setCBS(e.target.value);
              }}
            >
              {selectType(cbsOptions)}
            </select>
          </div>
          <div className="benchmark-upload-control-row">
            <div className="benchmark-upload-control-label">
              Source Type:
              <CustomTooltip
                key="sourcetype_tooltip"
                tooltipHtml={[<SourceTypeTooltip key="sourcetype_toolhtml" />]}
                tooltipIcon={[
                  <InformationTooltipHtml key="sourcetype_toolicon" />,
                ]}
                iconText={"Source Type"}
              />
            </div>
            <select
              value={sourceType}
              className="benchmark-upload-control-input"
              onChange={(e) => {
                setSourceType(e.target.value);
              }}
            >
              {selectType(sourceTypes())}
            </select>
          </div>
          <div className="benchmark-upload-control-row">
            <div className="benchmark-upload-control-label">
              Measurement Standard:
            </div>
            <select
              value={measurementSystem}
              className="benchmark-upload-control-input"
              onChange={(e) => {
                setMeasurementSystem(e.target.value);
              }}
            >
              {selectType(measurementSystems())}
            </select>
          </div>
          <div className="benchmark-upload-control-row">
            <div className="benchmark-upload-control-label">
              Information Sensitivity Level:
              <CustomTooltip
                key="infoseclevel_tooltip"
                tooltipHtml={[
                  <InformationSensitivityTooltip key="infoseclevel_toolhtml" />,
                ]}
                tooltipIcon={[
                  <InformationTooltipHtml key="infoseclevel_toolicon" />,
                ]}
                iconText={"Information Sensitivity Level"}
              />
            </div>
            <select
              value={infoSensLevel}
              className="benchmark-upload-control-input"
              onChange={(e) => {
                setInfoSensLevel(e.target.value);
              }}
            >
              {selectType(secLevelOptions)}
            </select>
          </div>
          {
            <div className="benchmark-upload-control-row">
              <div className="benchmark-upload-control-label">
                Client / Company:
              </div>

              <ModalCompany
                Component={CreateProjectSelectClientCompany}
                setFunction={(company) => {
                  setCompany(company);
                  setCompanyId(company.id);
                }}
                label={<i className="fas fa-building"></i>}
                company={company}
                error={""}
                title={"Client Company"}
                isProjectCreation={true}
              />
            </div>
          }
          <ProjectLocation setCoordinates={setCoordinates} />
        </div>
      </div>

      <div className="benchmark-upload-button-container">
        <FileTray files={filePreview} messages={messages} />
      </div>
      <div className="general-row-container">
        <DisplayError error={error} setError={setError} />
      </div>
      <div className="general-button-container">
        <button
          className="general-upload-button"
          onClick={() => {
            setMessages([]);
            onSubmit(data);
          }}
        >
          Upload
        </button>
      </div>
    </div>
  );
}

function DisplayError(props) {
  const { error } = props;
  return (
    <div className="benchmark-upload-error-container">
      <div className={error.type}>
        <b>{error.text}</b>
      </div>
    </div>
  );
}

export function Upload(props) {
  const { setFile, setFilePreview } = props;
  const { highlight, setHighlight } = props;
  const { setError, setMessages } = props;
  const { inputKey, setInputKey } = props; // Change to reset the File Input

  return (
    <DragDropUploadFile
      inputKey={inputKey}
      setInputKey={setInputKey}
      setFile={setFile}
      setFilePreview={setFilePreview}
      setHighlight={setHighlight}
      highlight={highlight}
      setError={setError}
      setMessages={setMessages}
      allowMultiple={true}
    />
  );
}

export function FileTray(props) {
  const { files } = props;
  const { messages } = props;
  let display = [];
  files.forEach((file, i) => {
    display.push(
      <FileTrayRow key={i} i={i} file={file} message={messages[i]} />
    );
  });

  return <div className="benchmark-upload-files">{display}</div>;
}

FileTrayRow.propTypes = {
  file: PropTypes.object.isRequired,
  message: PropTypes.object.isRequired,
  i: PropTypes.number.isRequired,
};

function FileTrayRow(props) {
  const { file } = props;
  const { message } = props;
  const { i } = props;
  const isFileRejected = message && message.status === "rejected";
  let messageClass = "benchmark-upload-message";

  if (isFileRejected) {
    messageClass = "benchmark-upload-message-error";
  }

  return (
    <>
      <div className="benchmark-upload-files-item">
        <b>
          <div className="benchmark-upload-files-part">
            {`Benchmark ${i + 1}`}
          </div>
        </b>
        <div className="benchmark-upload-files-name">{file.name}</div>
        {message && message.reason && (
          <div className={messageClass}>
            {isFileRejected ? "Error(s):" : message.reason.message}
          </div>
        )}
      </div>
      {isFileRejected && message?.reason && (
        <div>
          <pre className="benchmark-upload-file-errors">
            {message.reason.message}
          </pre>
        </div>
      )}
    </>
  );
}

export function selectType(array) {
  return array?.map((item, i) => {
    return (
      <option key={i} value={item.value}>
        {item.label}
      </option>
    );
  });
}

function sourceTypes() {
  return [
    { label: "Select Source", value: "" },

    {
      label: "Elemental Analysis (Complete)",
      value: "Elemental Analysis (Complete)",
    },
    {
      label: "Elemental Analysis (Estimate Only)",
      value: "Elemental Analysis (Estimate Only)",
    },
  ];
}

function measurementSystems() {
  return [
    { label: "Select System", value: "" },

    {
      label: "Metric",
      value: "METRIC",
    },
    {
      label: "Imperial",
      value: "IMPERIAL",
    },
  ];
}

export function SourceTypeTooltip(props) {
  return (
    <div key="source_type_toolhtml">
      <p>
        <b>Elemental Analysis (Complete):</b> Detailed breakdown and cost plan
        available for reference.
      </p>
      <p>
        <b>Elemental Analysis (Estimate Only):</b> High-level analysis
        available. Include "Est" in the tab name (e.g., EA (NRM) Est).
      </p>
    </div>
  );
}

export function InformationSensitivityTooltip(props) {
  return (
    <div key="information_sensitivity_toolhtml">
      <b>Strict NDA:</b>
      <p>
        Visible only to project members and benchmarkable with projects from the
        same client.
      </p>
      <b>Commercially Sensitive:</b>
      <p>
        Some information is anonymized; authorized users see all details. Can be
        benchmarked without displaying sensitive data.
      </p>
      <b>Arcadis Owned Data:</b>
      <p> All project information is accessible to every user.</p>
    </div>
  );
}
